import React, { useMemo, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, ColumnLayout, DatePicker, FormField, Input,
  Modal, Select, SpaceBetween,
} from '@cloudscape-design/components';
import { getCurrencyInputDisplayValue, parseCurrencyAmount } from '../../../utils/currencyFormat';

const { currentFiscalYear } = require('../../../lib/budgetFiscal');
const { generateExpenseOccurrences, fiscalYearEnd, normalizeExpenseSchedule } = require('../../../lib/expenseRecurrence');

const yearLabel = year => year.slice(0, 4) + '/' + year.slice(-2);
const money = value => new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(value);
const today = () => new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/Toronto', year: 'numeric', month: '2-digit', day: '2-digit',
}).format(new Date());
const frequencies = [{ value: 'weekly', label: 'Weeks' }, { value: 'monthly', label: 'Months' }, { value: 'yearly', label: 'Years' }];
const defaultEnd = date => { try { return fiscalYearEnd(date); } catch (_) { return ''; } };

function initialValues(item, initialPot, potChoices = []) {
  const date = item?.date || today();
  const originalPotId = item?.budgetPotId;
  const representative = potChoices.find(option => String(option.value) === String(originalPotId)
    || option.fiscalPotIds?.some(id => String(id) === String(originalPotId))
    || (item?.familyKey && option.familyKey === item.familyKey));
  return { description: item?.description || '',
    budgetPotId: String(representative?.value || originalPotId || initialPot || ''),
    amount: item ? String(item.amount) : '', date,
    recurring: false, frequency: 'monthly', interval: '1', endDate: defaultEnd(date) };
}

export default function ExpenseForm({ item, potChoices, initialPot, busy, error, onDismiss, onSave }) {
  const [values, setValues] = useState(() => initialValues(item, initialPot, potChoices));
  const [amountFocused, setAmountFocused] = useState(false);
  const [errors, setErrors] = useState({});
  const [endEdited, setEndEdited] = useState(!!item);
  const change = (key, value) => {
    setValues(previous => ({ ...previous, [key]: value,
      ...(key === 'date' && !endEdited ? { endDate: defaultEnd(value) } : {}) }));
    setErrors(previous => ({ ...previous, [key]: '' }));
  };
  const choices = useMemo(() => {
    const normalized = potChoices.map(option => ({ ...option, value: String(option.value) }));
    if (item && !normalized.some(option => option.value === String(item.budgetPotId) || option.fiscalPotIds?.some(id => String(id) === String(item.budgetPotId)) || option.familyKey === item.familyKey)) {
      const family = normalized.find(option => option.familyKey === item.familyKey);
      normalized.push({ value: String(item.budgetPotId), label: family?.label || item.budgetPotName });
    }
    return normalized;
  }, [potChoices, item]);
  const scheduleInput = useMemo(() => ({ ...values,
    amount: parseCurrencyAmount(values.amount), interval: Number(values.interval),
  }), [values]);
  const preview = useMemo(() => {
    try {
      const schedule = normalizeExpenseSchedule(scheduleInput);
      const occurrences = generateExpenseOccurrences(schedule);
      const currentYear = currentFiscalYear();
      const future = new Map();
      for (const occurrence of occurrences) {
        if (occurrence.fiscalYear > currentYear) future.set(occurrence.fiscalYear,
          (future.get(occurrence.fiscalYear) || 0) + occurrence.amountCents);
      }
      return [...future.entries()].map(([fiscalYear, cents]) => ({ fiscalYear, amount: cents / 100 }));
    } catch (_) { return []; }
  }, [scheduleInput]);
  const submit = () => {
    const fieldErrors = {};
    if (!values.description.trim()) fieldErrors.description = 'Enter a description.';
    if (!values.budgetPotId) fieldErrors.budgetPotId = 'Select a budget pot.';
    try {
      const schedule = normalizeExpenseSchedule(scheduleInput);
      generateExpenseOccurrences(schedule);
    } catch (failure) {
      fieldErrors.schedule = failure.message || 'Check the amount and dates.';
    }
    setErrors(fieldErrors);
    if (Object.keys(fieldErrors).length) return;
    const entry = { description: values.description.trim(), budgetPotId: values.budgetPotId,
      amount: parseCurrencyAmount(values.amount), date: values.date };
    onSave(item ? { ...entry, scope: 'this', version: item.version }
      : { ...entry, recurring: values.recurring, frequency: values.frequency,
        interval: Number(values.interval), endDate: values.endDate });
  };
  return <Modal visible onDismiss={() => { if (!busy) onDismiss(); }} size="medium"
    header={item ? 'Edit expense' : 'Add expense'} closeAriaLabel="Close expense form"
    footer={<Box float="right"><SpaceBetween direction="horizontal" size="xs">
      <Button variant="link" disabled={busy} onClick={onDismiss}>Cancel</Button>
      <Button variant="primary" loading={busy} onClick={submit}>{item ? 'Save changes' : 'Add expense'}</Button>
    </SpaceBetween></Box>}>
    <SpaceBetween size="m">
      {!!error && <Alert type="error">{error}</Alert>}
      <FormField label="Description" errorText={errors.description}>
        <Input ariaLabel="Description" value={values.description} spellcheck
          disabled={busy} onChange={({ detail }) => change('description', detail.value)} />
      </FormField>
      <FormField label="Budget pot" errorText={errors.budgetPotId}>
        <Select ariaLabel="Expense budget pot" placeholder="Choose a budget pot" filteringType="auto"
          selectedOption={choices.find(option => option.value === values.budgetPotId) || null}
          options={choices} disabled={busy} onChange={({ detail }) => change('budgetPotId', detail.selectedOption.value)} />
      </FormField>
      <ColumnLayout columns={2}>
        <FormField label={values.recurring ? 'Amount per expense' : 'Amount'}>
          <Input ariaLabel="Amount" inputMode="decimal" spellcheck={false} disabled={busy}
            value={getCurrencyInputDisplayValue(values.amount, amountFocused)}
            onChange={({ detail }) => change('amount', detail.value)}
            onFocus={() => setAmountFocused(true)} onBlur={() => setAmountFocused(false)} />
        </FormField>
        <FormField label={values.recurring ? 'Start date' : 'Date'}>
          <DatePicker ariaLabel={values.recurring ? 'Start date' : 'Date'} value={values.date}
            disabled={busy} placeholder="YYYY/MM/DD" onChange={({ detail }) => change('date', detail.value)} />
        </FormField>
      </ColumnLayout>
      {!item && <Checkbox checked={values.recurring} disabled={busy}
        onChange={({ detail }) => change('recurring', detail.checked)}>Recurring</Checkbox>}
      {!item && values.recurring && <ColumnLayout columns={2}>
        <FormField label="Repeat every">
          <SpaceBetween direction="horizontal" size="xs">
            <div style={{ width: '5rem' }}><Input ariaLabel="Repeat interval" value={values.interval} type="number" inputMode="numeric"
              spellcheck={false} disabled={busy} onChange={({ detail }) => change('interval', detail.value)} /></div>
            <Select ariaLabel="Repeat frequency" options={frequencies} selectedOption={frequencies.find(option => option.value === values.frequency)}
              disabled={busy} onChange={({ detail }) => change('frequency', detail.selectedOption.value)} />
          </SpaceBetween>
        </FormField>
        <FormField label="End date">
          <DatePicker ariaLabel="End date" value={values.endDate} disabled={busy} placeholder="YYYY/MM/DD"
            onChange={({ detail }) => { setEndEdited(true); change('endDate', detail.value); }} />
        </FormField>
      </ColumnLayout>}
      {errors.schedule && <Alert type="error">{errors.schedule}</Alert>}
      {preview.length > 0 && <Alert type="warning">
        {preview.map(row => <div key={row.fiscalYear}>{money(row.amount)} will be assigned to the future fiscal year {yearLabel(row.fiscalYear)}.</div>)}
      </Alert>}
    </SpaceBetween>
  </Modal>;
}
